import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, TextField, Typography, Grid, FormControl,
  InputLabel, Select, MenuItem, IconButton, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, FormControlLabel, Switch, InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi, {
  MillingRecipe, MillingRecipeInputRow, MillingRecipeOutputRow,
  RecipeStatus, OutputType
} from '../../services/millingApi';

const emptyInputRow: Partial<MillingRecipeInputRow> = {
  lineNo: 1,
  itemId: '',
  itemCode: '',
  itemName: '',
  uom: 'KG',
  standardQty: 0,
  isOptional: false,
  warehouseId: '',
  warehouseName: '',
  unitCost: 0,
};

const emptyOutputRow: Partial<MillingRecipeOutputRow> = {
  lineNo: 1,
  itemId: '',
  itemCode: '',
  itemName: '',
  uom: 'KG',
  outputType: 'main_product',
  isByProduct: false,
  isMainProduct: true,
  expectedQty: 0,
  expectedYieldPercent: 0,
  costAllocationPercent: 100,
  warehouseId: '',
  warehouseName: '',
  unitValue: 0,
  marketPrice: 0,
};

const MillingRecipeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id) && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<MillingRecipe>>({
    recipeCode: '',
    recipeName: '',
    description: '',
    version: '1.0',
    inputItemId: '',
    inputItemCode: '',
    inputItemName: '',
    inputUom: 'KG',
    standardInputQty: 0,
    outputItemId: '',
    outputItemCode: '',
    outputItemName: '',
    outputUom: 'KG',
    standardOutputQty: 0,
    expectedYieldPercent: 0,
    minYieldPercent: 0,
    maxYieldPercent: 100,
    standardLaborCost: 0,
    standardOverheadCost: 0,
    standardDepreciationCost: 0,
    standardUtilitiesCost: 0,
    processingTimeMinutes: 0,
    status: 'draft',
    isDefault: false,
    createdBy: 'admin',
  });

  const [inputRows, setInputRows] = useState<Partial<MillingRecipeInputRow>[]>([]);
  const [outputRows, setOutputRows] = useState<Partial<MillingRecipeOutputRow>[]>([]);

  useEffect(() => {
    if (isEdit) {
      fetchRecipe();
    }
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const recipe = await millingApi.getRecipe(id!);
      setFormData(recipe);
      setInputRows(recipe.inputRows || []);
      setOutputRows(recipe.outputRows || []);
    } catch (err) {
      console.error(err);
      setError('Error loading recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof MillingRecipe, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputRowChange = (index: number, field: keyof MillingRecipeInputRow, value: any) => {
    setInputRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleOutputRowChange = (index: number, field: keyof MillingRecipeOutputRow, value: any) => {
    setOutputRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-update flags based on outputType
      if (field === 'outputType') {
        updated[index].isByProduct = value === 'by_product';
        updated[index].isMainProduct = value === 'main_product';
      }
      return updated;
    });
  };

  const addInputRow = () => {
    setInputRows((prev) => [...prev, { ...emptyInputRow, lineNo: prev.length + 1 }]);
  };

  const removeInputRow = (index: number) => {
    setInputRows((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, lineNo: i + 1 })));
  };

  const addOutputRow = () => {
    setOutputRows((prev) => [...prev, { ...emptyOutputRow, lineNo: prev.length + 1, outputType: 'by_product', isByProduct: true, isMainProduct: false }]);
  };

  const removeOutputRow = (index: number) => {
    setOutputRows((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, lineNo: i + 1 })));
  };

  const calculateYield = () => {
    if (formData.standardInputQty && formData.standardInputQty > 0 && formData.standardOutputQty) {
      const yieldPercent = (formData.standardOutputQty / formData.standardInputQty) * 100;
      handleChange('expectedYieldPercent', Math.round(yieldPercent * 100) / 100);
    }
  };

  const calculateTotalCost = () => {
    const total = (formData.standardLaborCost || 0) +
      (formData.standardOverheadCost || 0) +
      (formData.standardDepreciationCost || 0) +
      (formData.standardUtilitiesCost || 0);
    return total;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.recipeCode || !formData.recipeName) {
      setError('Recipe Code and Name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload: any = {
        ...formData,
        inputRows: inputRows.map((row, idx) => ({ ...row, lineNo: idx + 1 })),
        outputRows: outputRows.map((row, idx) => ({ ...row, lineNo: idx + 1 })),
        standardTotalCost: calculateTotalCost(),
      };

      if (isEdit) {
        await millingApi.updateRecipe(id!, payload);
      } else {
        await millingApi.createRecipe(payload);
      }
      navigate('/milling/recipes');
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Error saving recipe');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/milling/recipes')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">{isEdit ? 'Edit Recipe' : 'New Milling Recipe'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Recipe Code"
                  value={formData.recipeCode}
                  onChange={(e) => handleChange('recipeCode', e.target.value)}
                  disabled={isEdit}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  required
                  label="Recipe Name"
                  value={formData.recipeName}
                  onChange={(e) => handleChange('recipeName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Version"
                  value={formData.version}
                  onChange={(e) => handleChange('version', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleChange('status', e.target.value)}
                    disabled={!isEdit}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="obsolete">Obsolete</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Primary Input/Output */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Primary Input & Output</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Input (Raw Material)</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Item Code"
                      value={formData.inputItemCode}
                      onChange={(e) => handleChange('inputItemCode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="Item Name"
                      value={formData.inputItemName}
                      onChange={(e) => handleChange('inputItemName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Standard Qty"
                      value={formData.standardInputQty}
                      onChange={(e) => handleChange('standardInputQty', parseFloat(e.target.value) || 0)}
                      onBlur={calculateYield}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="UOM"
                      value={formData.inputUom}
                      onChange={(e) => handleChange('inputUom', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>Output (Main Product)</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Item Code"
                      value={formData.outputItemCode}
                      onChange={(e) => handleChange('outputItemCode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="Item Name"
                      value={formData.outputItemName}
                      onChange={(e) => handleChange('outputItemName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Standard Qty"
                      value={formData.standardOutputQty}
                      onChange={(e) => handleChange('standardOutputQty', parseFloat(e.target.value) || 0)}
                      onBlur={calculateYield}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="UOM"
                      value={formData.outputUom}
                      onChange={(e) => handleChange('outputUom', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Expected Yield %"
                  value={formData.expectedYieldPercent}
                  onChange={(e) => handleChange('expectedYieldPercent', parseFloat(e.target.value) || 0)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min Yield %"
                  value={formData.minYieldPercent}
                  onChange={(e) => handleChange('minYieldPercent', parseFloat(e.target.value) || 0)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Yield %"
                  value={formData.maxYieldPercent}
                  onChange={(e) => handleChange('maxYieldPercent', parseFloat(e.target.value) || 0)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Additional Inputs */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Additional Input Materials</Typography>
              <Button startIcon={<AddIcon />} onClick={addInputRow} size="small">Add Input</Button>
            </Box>
            {inputRows.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Std Qty</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="center">Optional</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inputRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemCode} onChange={(e) => handleInputRowChange(idx, 'itemCode', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemName} onChange={(e) => handleInputRowChange(idx, 'itemName', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.uom} onChange={(e) => handleInputRowChange(idx, 'uom', e.target.value)} sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.standardQty} onChange={(e) => handleInputRowChange(idx, 'standardQty', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.unitCost} onChange={(e) => handleInputRowChange(idx, 'unitCost', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Switch checked={row.isOptional} onChange={(e) => handleInputRowChange(idx, 'isOptional', e.target.checked)} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeInputRow(idx)}><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {inputRows.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No additional inputs. Click "Add Input" to add materials.</Typography>
            )}
          </CardContent>
        </Card>

        {/* By-Products / Secondary Outputs */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">By-Products & Secondary Outputs</Typography>
              <Button startIcon={<AddIcon />} onClick={addOutputRow} size="small">Add Output</Button>
            </Box>
            {outputRows.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Expected Qty</TableCell>
                      <TableCell align="right">Yield %</TableCell>
                      <TableCell align="right">Cost Alloc %</TableCell>
                      <TableCell align="right">Market Price</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outputRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemCode} onChange={(e) => handleOutputRowChange(idx, 'itemCode', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={row.itemName} onChange={(e) => handleOutputRowChange(idx, 'itemName', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Select size="small" value={row.outputType} onChange={(e) => handleOutputRowChange(idx, 'outputType', e.target.value as OutputType)}>
                            <MenuItem value="main_product">Main Product</MenuItem>
                            <MenuItem value="by_product">By-Product</MenuItem>
                            <MenuItem value="waste">Waste</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.expectedQty} onChange={(e) => handleOutputRowChange(idx, 'expectedQty', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.expectedYieldPercent} onChange={(e) => handleOutputRowChange(idx, 'expectedYieldPercent', parseFloat(e.target.value) || 0)} sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.costAllocationPercent} onChange={(e) => handleOutputRowChange(idx, 'costAllocationPercent', parseFloat(e.target.value) || 0)} sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={row.marketPrice} onChange={(e) => handleOutputRowChange(idx, 'marketPrice', parseFloat(e.target.value) || 0)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeOutputRow(idx)}><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {outputRows.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No by-products. Click "Add Output" to add secondary outputs.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Standard Costs */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Standard Costs</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Labor Cost"
                  value={formData.standardLaborCost}
                  onChange={(e) => handleChange('standardLaborCost', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Overhead Cost"
                  value={formData.standardOverheadCost}
                  onChange={(e) => handleChange('standardOverheadCost', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Depreciation"
                  value={formData.standardDepreciationCost}
                  onChange={(e) => handleChange('standardDepreciationCost', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Utilities"
                  value={formData.standardUtilitiesCost}
                  onChange={(e) => handleChange('standardUtilitiesCost', parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  disabled
                  label="Total Cost"
                  value={calculateTotalCost().toFixed(2)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Processing Time"
                  value={formData.processingTimeMinutes}
                  onChange={(e) => handleChange('processingTimeMinutes', parseInt(e.target.value) || 0)}
                  InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Switch checked={formData.isDefault} onChange={(e) => handleChange('isDefault', e.target.checked)} />}
                label="Set as default recipe"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/milling/recipes')}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (isEdit ? 'Update Recipe' : 'Create Recipe')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default MillingRecipeForm;
